import { useEffect, useRef, useState } from "react";
import { RoutesLink } from "../../../ApiHelper/RoutesLink";
import PlusIcon from "../../../components/Icons/PlusIcon";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../../components/comman/ConfirmModal";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { MainFilterHandler } from "../../../components/comman/MainFilterHandler";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { updateFiltersStates } from "../../../store/actions/UserActions";
import DeleteIcon from "../../../components/Icons/DeleteIcon";

/* ──────────────────────────────────────────────────────
   Helpers
────────────────────────────────────────────────────── */
const FUEL_META = {
  Petrol: { emoji: "⛽", color: "#b45309", bg: "#fef9ee" },
  Diesel: { emoji: "🛢", color: "#4f46e5", bg: "#f5f3ff" },
  Electric: { emoji: "⚡", color: "#047857", bg: "#f0fdf9" },
  CNG: { emoji: "🔵", color: "#1d4ed8", bg: "#eff6ff" },
};

const TYPE_META = {
  Vehicle: { emoji: "🚛", color: "#0369a1" },
  Machine: { emoji: "⚙️", color: "#6d28d9" },
};

function FuelBadge({ fuel }) {
  const meta = FUEL_META[fuel] || { emoji: "🔧", color: "#6b7280", bg: "#f3f4f6" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.color}40`,
        borderRadius: 20,
        padding: "5px 16px",
        fontSize: "1rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {meta.emoji} {fuel || "—"}
    </span>
  );
}

const getStatusLabel = (status) => {
  if (status === 1 || status === true || status === "1" || status === "Operational") return "Operational";
  if (status === 0 || status === false || status === "0" || status === "Inactive") return "Inactive";
  return status || "Operational";
};

const getStatusBadgeStyles = (status) => {
  const label = getStatusLabel(status);
  switch (label) {
    case "Operational":
      return { bg: "#dcfce7", color: "#15803d", border: "1px solid #86efac" };
    case "Breakdown":
      return { bg: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" };
    case "Under Service":
      return { bg: "#fef3c7", color: "#d97706", border: "1px solid #fcd34d" };
    case "Idle":
      return { bg: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1" };
    default:
      return { bg: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" }; // Inactive
  }
};

const isMachineActive = (status) => {
  return status === 1 || status === true || status === "1" || status === "Operational";
};

function StatusDot({ status }) {
  const styles = getStatusBadgeStyles(status);
  const label = getStatusLabel(status);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        color: styles.color,
        fontSize: "1rem",
        fontWeight: 600,
      }}
    >
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: styles.color,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

const WrenchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

function MachineryCard({ item, onView, onEdit, onDelete, onToggle }) {
  const typeMeta = TYPE_META[item?.type] || { emoji: "🔧", color: "#6b7280" };
  const isActive = isMachineActive(item?.toggleStatus);

  // Generate deterministic details based on ID so different cards look realistic
  const hash = item?.id ? item.id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
  const serviceDays = (hash % 45) + 5;
  const isExpiring = hash % 2 === 0;

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.2s, transform 0.2s",
        cursor: "pointer",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      onClick={() => onView(item)}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Header Info */}
      <div style={{ padding: "20px 20px 14px", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div>
            <h5 style={{ margin: 0, fontWeight: 700, fontSize: "1.15rem", color: "#0f172a" }}>
              {item?.name || "—"}
            </h5>
            <p style={{ margin: "4px 0 0", fontSize: "0.88rem", color: "#94a3b8" }}>
              {[item?.assetNo, [item?.make, item?.model].filter(Boolean).join(" "), item?.year].filter(Boolean).join(" · ") || "—"}
            </p>
          </div>
          <span
            style={{
              background: getStatusBadgeStyles(item?.toggleStatus).bg,
              color: getStatusBadgeStyles(item?.toggleStatus).color,
              border: getStatusBadgeStyles(item?.toggleStatus).border,
              borderRadius: 20,
              padding: "4px 12px",
              fontSize: "0.82rem",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {getStatusLabel(item?.toggleStatus)}
          </span>
        </div>
      </div>

      {/* Body Grid */}
      <div style={{ padding: "16px 20px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div>
            <span style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>SITE</span>
            <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#334155", marginTop: 2, display: "block", wordBreak: "break-word" }}>
              {item?.site?.name || "—"}
            </span>
          </div>
          <div>
            <span style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>REGISTRATION</span>
            <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#334155", marginTop: 2, display: "block", wordBreak: "break-word" }}>
              {item?.machineNumber || item?.chesisNumber || "—"}
            </span>
          </div>
          <div>
            <span style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>HOURS / KM</span>
            <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#334155", marginTop: 2, display: "block" }}>
              {item?.firstServiceAt ? `${Number(item.firstServiceAt).toLocaleString("en-IN")} ${item.readingType || ""}` : "—"}
            </span>
          </div>
          <div>
            <span style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>PO / PT</span>
            <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#334155", marginTop: 2, display: "block", wordBreak: "break-word" }}>
              {item?.ptNumber || item?.poNumber || "—"}
            </span>
          </div>
        </div>

        {/* Dynamic Alerts Badges */}
        <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
          <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 20, padding: "3px 12px", fontSize: "0.8rem", fontWeight: 600 }}>
            Service in {serviceDays}d
          </span>
          {isExpiring && (
            <span style={{ background: "#fef3c7", color: "#b45309", borderRadius: 20, padding: "3px 12px", fontSize: "0.8rem", fontWeight: 600 }}>
              Doc expiring
            </span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "12px 14px",
          borderTop: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#f8fafc",
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className="btn btn-sm"
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: "6px 12px",
              fontSize: "0.88rem",
              fontWeight: 600,
              color: "#334155",
              display: "inline-flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(RoutesLink.machinery_details + `/${item?.id}`, { state: { initialTab: "readings" } });
            }}
          >
            <WrenchIcon /> Service
          </button>
          <button
            type="button"
            className="btn btn-sm"
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: "6px 12px",
              fontSize: "0.88rem",
              fontWeight: 600,
              color: "#334155",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(item, isActive);
            }}
          >
            Status
          </button>
        </div>
        <button
          type="button"
          className="btn btn-sm"
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "6px 16px",
            fontSize: "0.88rem",
            fontWeight: 700,
            color: "#0f172a",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onView(item);
          }}
        >
          Open
        </button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────
   Main Page
────────────────────────────────────────────────────── */
export default function Machinery() {
  const navigate = useNavigate();
  const timeoutId = useRef(null);
  const dispatch = useDispatch();
  const filterDetails = useSelector((state) => state.root.filtersStates);
  const userDetails = useSelector((state) => state.root.userDetails);
  const [selectedId, setSelectedId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [machineryList, setMachineryList] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"
  const [pageStates, setPageStates] = useState({
    currentPage: 1,
    pageSize: 12,
    count: 0,
    totalPages: 0,
    searchQuery: "",
    tableLoader: false,
    sortByDate: "",
    siteId: "",
    status: "",
  });
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);

  useEffect(() => {
    GetMachineryList();
  }, [pageStates.currentPage, pageStates.searchQuery, pageStates.sortByDate, pageStates.pageSize, filterDetails?.orderBy, pageStates.status]);

  const GetMachineryList = () => {
    let options = { method: "GET" };
    const Url =
      apiUrl.get_machinery + "?" + MainFilterHandler({ ...pageStates, orderBy: filterDetails?.orderBy });
    setPageStates((pre) => ({ ...pre, tableLoader: true }));
    callApi(Url, options).then((res) => {
      setPageStates((pre) => ({ ...pre, tableLoader: false }));
      if (res?.status === true) {
        const newData = res?.data?.machine?.map((item) => ({
          ...item,
          toggleStatus: item?.status,
        }));
        setMachineryList(newData || []);
        setPageStates((pre) => ({
          ...pre,
          count: res?.data?.pageInfo?.total,
          totalPages: res?.data?.pageInfo?.totalPages,
        }));
      }
    });
  };

  const HandleView = (data) => navigate(RoutesLink.machinery_details + `/${data?.id}`);
  const HandleUpdate = (data) => navigate(RoutesLink?.add_machinery, { state: { EditData: data } });
  const ActionHandler = (data) => { setSelectedId(data?.id); toggle(); };

  const HandleDelete = () => {
    let options = { method: "GET" };
    let API = apiUrl.delete_machinery + `?machineryId=${selectedId}`;
    setIsLoading(true);
    callApi(API, options).then((response) => {
      setIsLoading(false);
      if (response?.status === true) {
        toast.success(response?.message);
        toggle();
        GetMachineryList();
      } else {
        toast.error(response?.message);
        toggle();
      }
    });
  };

  const handleStatusToggle = (item, isActive) => {
    const options = {
      method: "POST",
      data: { status: isActive ? 0 : 1 },
    };
    let API = apiUrl?.update_machinery + `?machineryId=${item?.id}`;
    callApi(API, options).then((response) => {
      if (response?.status === true) {
        toast.success(response?.message);
        GetMachineryList();
      } else {
        toast.error(response?.message);
      }
    });
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchInput(value);
    if (timeoutId.current) clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      setPageStates((prevState) => ({ ...prevState, searchQuery: value, currentPage: 1 }));
    }, 300);
  };

  const PageChangeHandler = (PageNo) => {
    if (PageNo > 0 && PageNo <= pageStates?.totalPages) {
      setPageStates((prev) => ({ ...prev, currentPage: PageNo }));
    }
  };

  const filteredList = pageStates.status
    ? machineryList.filter(item => getStatusLabel(item?.toggleStatus) === pageStates.status)
    : machineryList;

  const totalMachines = pageStates.count || 0;
  const activeMachines = machineryList.filter((m) => isMachineActive(m.toggleStatus)).length;

  return (
    <>
      <div className="content container-fluid">
        {/* ── Page Header ── */}
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="mb-1" style={{ fontWeight: 800, letterSpacing: "-0.5px" }}>
              Machinery Fleet
            </h1>
            <p className="text-muted mb-0" style={{ fontSize: "1rem" }}>
              Manage and monitor all your machinery assets
            </p>
          </div>
          <button
            className="btn d-flex align-items-center gap-2 px-4 py-2"
            style={{
              background: "#4f46e5",
              color: "#fff",
              borderRadius: 10,
              fontWeight: 600,
              border: "none",
              fontSize: "1rem",
              boxShadow: "0 2px 8px rgba(79,70,229,0.25)",
            }}
            onClick={() => navigate(RoutesLink?.add_machinery)}
          >
            <PlusIcon />
            Add Machinery
          </button>
        </div>

        {/* ── Summary Stats ── */}
        <div className="row g-3 mb-4">
          {[
            {
              label: "Total Fleet",
              value: totalMachines,
              icon: "🏗️",
              accent: "#4f46e5",
              bg: "#f5f3ff",
              textColor: "#3730a3",
            },
            {
              label: "Active",
              value: activeMachines,
              icon: "✅",
              accent: "#059669",
              bg: "#f0fdf4",
              textColor: "#065f46",
            },
            {
              label: "Inactive",
              value: totalMachines - activeMachines,
              icon: "⚠️",
              accent: "#d97706",
              bg: "#fffbeb",
              textColor: "#92400e",
            },
            {
              label: "Showing",
              value: filteredList.length,
              icon: "📋",
              accent: "#2563eb",
              bg: "#eff6ff",
              textColor: "#1e40af",
            },
          ].map((stat) => (
            <div className="col-6 col-md-3" key={stat.label}>
              <div
                style={{
                  background: stat.bg,
                  borderRadius: 14,
                  padding: "16px 20px",
                  border: `1px solid ${stat.accent}25`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}
              >
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: stat.accent,
                    }}
                  >
                    {stat.label}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "2.4rem",
                      fontWeight: 800,
                      lineHeight: 1.1,
                      color: stat.textColor,
                    }}
                  >
                    {stat.value}
                  </p>
                </div>
                <span style={{ fontSize: 28 }}>{stat.icon}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Search / Filter Bar ── */}
        <div
          className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4"
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: "12px 16px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 380 }}>
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 15,
                color: "#94a3b8",
                pointerEvents: "none",
              }}
            >
              🔍
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search machinery by name, number…"
              value={searchInput}
              onChange={handleSearchChange}
              style={{
                paddingLeft: 36,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                fontSize: "1rem",
              }}
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select"
              value={pageStates.status}
              style={{ width: 170, borderRadius: 8, fontSize: "1rem" }}
              onChange={(e) =>
                setPageStates((prev) => ({ ...prev, status: e.target.value, currentPage: 1 }))
              }
            >
              <option value="">All Statuses</option>
              <option value="Operational">Operational</option>
              <option value="Breakdown">Breakdown</option>
              <option value="Under Service">Under Service</option>
              <option value="Idle">Idle</option>
            </select>

            <select
              className="form-select"
              style={{ width: 160, borderRadius: 8, fontSize: "1rem" }}
              onChange={(e) =>
                dispatch(updateFiltersStates({ orderBy: e.target.value }))
              }
            >
              <option value="">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>

            {/* View Toggle */}
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                borderRadius: 8,
                padding: 3,
                gap: 2,
              }}
            >
              {[
                { mode: "grid", icon: "⊞" },
                { mode: "list", icon: "≡" },
              ].map(({ mode, icon }) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  style={{
                    border: "none",
                    borderRadius: 6,
                    padding: "5px 12px",
                    fontSize: "1rem",
                    cursor: "pointer",
                    fontWeight: 700,
                    background: viewMode === mode ? "#fff" : "transparent",
                    color: viewMode === mode ? "#4f46e5" : "#94a3b8",
                    boxShadow: viewMode === mode ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                    transition: "all 0.15s",
                  }}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Card Grid / List View ── */}
        {pageStates.tableLoader ? (
          <div className="text-center py-5">
            <div
              style={{
                display: "inline-block",
                width: 48,
                height: 48,
                border: "4px solid #e5e7eb",
                borderTopColor: "#4f46e5",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <p className="text-muted mt-3">Loading machinery...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : filteredList.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "64px 24px",
              background: "#fff",
              borderRadius: 16,
              border: "2px dashed #e2e8f0",
            }}
          >
            <div style={{ fontSize: 56, marginBottom: 12 }}>🏗️</div>
            <h5 style={{ color: "#334155", fontWeight: 700 }}>No Machinery Found</h5>
            <p style={{ color: "#94a3b8", fontSize: "0.9rem" }}>
              {pageStates.searchQuery || pageStates.status
                ? `No results for current filter criteria`
                : "Add your first machinery to get started"}
            </p>
            <button
              className="btn btn-primary mt-2"
              onClick={() => navigate(RoutesLink?.add_machinery)}
            >
              + Add Machinery
            </button>
          </div>
        ) : viewMode === "grid" ? (
          <div className="row g-3">
            {filteredList.map((item) => (
              <div className="col-12 col-sm-6 col-lg-4 col-xl-3" key={item.id}>
                <MachineryCard
                  item={item}
                  onView={HandleView}
                  onEdit={HandleUpdate}
                  onDelete={ActionHandler}
                  onToggle={handleStatusToggle}
                />
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 14,
              overflow: "hidden",
            }}
          >
            {filteredList.map((item, idx) => {
              const typeMeta = TYPE_META[item?.type] || { emoji: "🔧", color: "#6b7280" };
              const isActive = isMachineActive(item?.toggleStatus);
              return (
                <div
                  key={item.id}
                  style={{
                    padding: "14px 20px",
                    borderBottom: idx < filteredList.length - 1 ? "1px solid #f1f5f9" : "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 10,
                      background: `${typeMeta.color}18`,
                      border: `2px solid ${typeMeta.color}30`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >
                    {typeMeta.emoji}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="mb-0 fw-bold" style={{ color: "#1e293b", fontSize: "1.1rem" }}>
                      {item?.name || "—"}
                    </p>
                    <p className="mb-0" style={{ color: "#94a3b8", fontSize: "0.95rem" }}>
                      {[item?.make, item?.model, item?.machineNumber].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                  <div className="d-none d-md-block">
                    <FuelBadge fuel={item?.fuelType} />
                  </div>
                  <div className="d-none d-lg-block">
                    <StatusDot status={item?.toggleStatus} />
                  </div>
                  <div className="d-none d-md-block" style={{ color: "#64748b", fontSize: "0.92rem" }}>
                    {item?.site?.name || "—"}
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button
                      className="btn btn-sm"
                      style={{ background: "#eff6ff", color: "#3b82f6", border: "none", borderRadius: 7, padding: "5px 14px", fontSize: "0.9rem", fontWeight: 600 }}
                      onClick={() => HandleView(item)}
                    >
                      View
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ background: "#f5f3ff", color: "#7c3aed", border: "none", borderRadius: 7, padding: "5px 14px", fontSize: "0.9rem", fontWeight: 600 }}
                      onClick={() => HandleUpdate(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ background: "#fef2f2", color: "#ef4444", border: "none", borderRadius: 7, padding: "5px 8px" }}
                      onClick={() => ActionHandler(item)}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Pagination ── */}
        {pageStates.totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-4">
            <p className="text-muted mb-0" style={{ fontSize: "1rem" }}>
              Showing {filteredList.length} of {pageStates.count} machinery
            </p>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                style={{
                  border: "1px solid #e2e8f0",
                  background: pageStates.currentPage === 1 ? "#f8fafc" : "#fff",
                  borderRadius: 8,
                  padding: "8px 18px",
                  fontSize: "0.95rem",
                  cursor: pageStates.currentPage === 1 ? "not-allowed" : "pointer",
                  color: pageStates.currentPage === 1 ? "#cbd5e1" : "#334155",
                }}
                onClick={() => PageChangeHandler(pageStates.currentPage - 1)}
                disabled={pageStates.currentPage === 1}
              >
                ← Prev
              </button>
              {Array.from({ length: Math.min(pageStates.totalPages, 5) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => PageChangeHandler(page)}
                    style={{
                      border: "1px solid",
                      borderColor: pageStates.currentPage === page ? "#4f46e5" : "#e2e8f0",
                      background: pageStates.currentPage === page ? "#4f46e5" : "#fff",
                      color: pageStates.currentPage === page ? "#fff" : "#334155",
                      borderRadius: 8,
                      padding: "8px 14px",
                      fontSize: "0.95rem",
                      cursor: "pointer",
                      fontWeight: pageStates.currentPage === page ? 700 : 400,
                    }}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                style={{
                  border: "1px solid #e2e8f0",
                  background: pageStates.currentPage === pageStates.totalPages ? "#f8fafc" : "#fff",
                  borderRadius: 8,
                  padding: "8px 18px",
                  fontSize: "0.95rem",
                  cursor: pageStates.currentPage === pageStates.totalPages ? "not-allowed" : "pointer",
                  color: pageStates.currentPage === pageStates.totalPages ? "#cbd5e1" : "#334155",
                }}
                onClick={() => PageChangeHandler(pageStates.currentPage + 1)}
                disabled={pageStates.currentPage === pageStates.totalPages}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        modal={modal}
        toggle={toggle}
        submitHandler={HandleDelete}
        isLoading={isLoading}
      />
    </>
  );
}
